import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, parseISO } from "date-fns";
import { useSettings } from "../../../../context/settings";
import { Col, FormGroup, Input, InputGroup, InputGroupText, Label, Row } from "reactstrap";
import Select from "react-select";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import sidebar from "../../utils/sidebar";
import FixedSave from "../../../../components/Actions/FixedSave";
import SelectCustomStyles from "../../../../utils/styles/reactSelect";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { get, post } from "../../../../utils/helpers/api";
import { toast } from "react-toastify";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";

function CreatePaymentPage() {
  const { t } = useTranslation();
  const { datePickerFormat } = useSettings();
  const { state } = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [initLoading, setInitLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [suppliersData, setSuppliersData] = useState([]);
  const [billsData, setBillsData] = useState([]);
  const [paymentData, setPaymentData] = useState({
    supplier_id: state?.paymentData?.supplier_id || "",
    bill_id: state?.paymentData?.bill_id || "",
    amount: state?.paymentData?.amount || "",
    currency: state?.paymentData?.currency || "usd",
    payment_date: state?.paymentData?.payment_date ? parseISO(state.paymentData.payment_date) : new Date(),
    payment_method: state?.paymentData?.payment_method || "",
    reference: state?.paymentData?.reference || "",
    notes: state?.paymentData?.notes || "",
  });
  const [companySettings, setCompanySettings] = useState({});

  useEffect(() => {
    initData();
  }, []);

  useEffect(() => {
    if (id && !state?.paymentData) {
      getPayment();
    }
  }, [id]);

  const initData = async () => {
    setInitLoading(true);
    const data = await get('/purchases/payments/create');
    if (data.success) {
      setSuppliersData(data.suppliers);
      setBillsData(data.bills);
      setCompanySettings(data.company_settings);
    }
    setInitLoading(false);
  };

  const getPayment = async () => {
    const data = await get('/purchases/payments/view/' + id);
    if (data?.data) {
      setPaymentData(data.data);
    }
  };

  const filteredBills = paymentData.supplier_id
    ? billsData.filter(b => b.supplier_id === paymentData.supplier_id)
    : billsData;

  const savePayment = async () => {
    if (!paymentData.supplier_id) {
      toast.error(t('purchases.selectSupplierRequired'));
      return;
    }
    if (!paymentData.bill_id) {
      toast.error(t('purchases.selectBillRequired'));
      return;
    }
    if (!paymentData.amount) {
      toast.error(t('purchases.amountRequired'));
      return;
    }
    setSaveLoading(true);
    const url = id ? '/purchases/payments/update/' + id : '/purchases/payments/store';
    const data = await post(url, {
      ...paymentData,
      payment_date: paymentData.payment_date ? format(paymentData.payment_date, 'yyyy-MM-dd') : null,
    });
    if (data.success) {
      navigate('/purchases/payments');
    } else {
      toast.error(data.message || t('common.somethingWentWrong'));
    }
    setSaveLoading(false);
  };

  const supplierOptions = suppliersData.map(s => ({
    value: s.id,
    label: s.company_name || [s.firstname, s.lastname].filter(Boolean).join(' ') || s.id
  }));

  const billOptions = filteredBills.map(b => ({
    value: b.id,
    label: b.bill_number + (b.total_amount ? ` — ${b.total_amount} ${b.currency?.toUpperCase()}` : '')
  }));

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader
          breadcrumb={[t('purchases.payments'), id ? t('purchases.editPayment') : t('purchases.newPayment')]}
          onBack={() => navigate('/purchases/payments')}
        />
        <div className="subheader-page-content scrollable">
          <Row>
            <Col md="6" className="mb-3">
              <Label>{t('purchases.supplier')} </Label>
              <Select
                options={supplierOptions}
                styles={SelectCustomStyles}
                isLoading={initLoading}
                value={supplierOptions.find(o => o.value === paymentData.supplier_id) || null}
                onChange={selected => setPaymentData({ ...paymentData, supplier_id: selected ? selected.value : '', bill_id: '' })}
              />
            </Col>
          </Row>

          <Row>
            <Col md="6" className="mb-3">
              <Label>{t('purchases.billTitle')}: </Label>
              <Select
                options={billOptions}
                styles={SelectCustomStyles}
                isLoading={initLoading}
                value={billOptions.find(o => o.value === paymentData.bill_id) || null}
                onChange={selected => setPaymentData({ ...paymentData, bill_id: selected ? selected.value : '', amount: selected ? filteredBills.find(b => b.id === selected.value)?.total_amount || '' : '' })}
                isDisabled={!paymentData.supplier_id}
                placeholder={paymentData.supplier_id ? t('purchases.selectBillPlaceholder') : t('purchases.selectSupplierFirstPlaceholder')}
              />
            </Col>
          </Row>

          <Row>
            <Col md="6" className="mb-3">
              <Label>{t('common.amount')}: </Label>
              <InputGroup>
                <InputGroupText>{ companySettings?.sales?.base_currency }</InputGroupText>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={paymentData.amount}
                  onChange={e => setPaymentData({ ...paymentData, amount: e.target.value })}
                />
              </InputGroup>
            </Col>
          </Row>

          <Row>
            <Col md="6" className="mb-3">
              <Label>{t('sales.paymentDate')}: </Label>
              <DatePicker
                selected={paymentData.payment_date}
                onChange={(d) => setPaymentData({ ...paymentData, payment_date: d })}
                className="form-control"
                dateFormat={datePickerFormat}
              />
            </Col>
          </Row>

          <Row>
            <Col md="6" className="mb-3">
              <Label>{t('sales.paymentMethod')}: </Label>
              <Input
                value={paymentData.payment_method}
                placeholder={t('purchases.paymentMethodPlaceholder')}
                onChange={e => setPaymentData({ ...paymentData, payment_method: e.target.value })}
              />
            </Col>
          </Row>

          <Row>
            <Col md="6" className="mb-3">
              <Label>{t('common.reference')}: </Label>
              <Input
                value={paymentData.reference}
                onChange={e => setPaymentData({ ...paymentData, reference: e.target.value })}
              />
            </Col>
          </Row>

          <FormGroup>
            <Label>{t('common.notes')}: </Label>
            <Input
              type="textarea"
              rows="4"
              value={paymentData.notes}
              onChange={e => setPaymentData({ ...paymentData, notes: e.target.value })}
            />
          </FormGroup>
        </div>
      </div>

      <FixedSave
        onSave={savePayment}
        loading={saveLoading}
        disabled={saveLoading || initLoading}
      />
    </SidebarLayout>
  );
}

export default CreatePaymentPage;
